import feathers from '@feathersjs/client';
import LRU from 'lru-cache';
import yup from 'src/feathers/yup';
const { FeathersError, GeneralError, BadRequest } = feathers.errors;

class ValidationError extends FeathersError {
  constructor(message, data) {
    super(message, 'validation-error', 420, 'ValidationError', data);
  }
}

const convertYupError = (yupError) => {
  if (yupError.inner.length === 0) {
    return {
      [yupError.path]: yupError.message
    };
  }
  return yupError.inner.reduce((errors, error) => {
    errors[error.path] = error.message;
    return errors;
  }, {});
};

// Wrap a hook declaration to make it skippable.
// Pass params 'skipHook' with either a string or array
// of strings that match `hookName`to skip those hooks.
// NOTE: you must wrap the HOOK, not a function that returns a hook...
export const skippable = (hookName, hookFunc) => {
  return (context) => {
    if (context.params.skipHooks) {
      const skipHooks = Array.isArray(context.params.skipHooks)
        ? context.params.skipHooks
        : [context.params.skipHooks];
      if (skipHooks.includes(hookName)) {
        return context;
      } else {
        return hookFunc(context);
      }
    } else {
      return hookFunc(context);
    }
  };
};

const handleNoName = (context) => {
  if (!context.service.name) {
    throw new Error(
      `Cannot call hook "setStore" on a service without a name at ${context.path} with method ${context.method}`
    );
  }
};

export const setStore = skippable('setStore', async (context) => {
  handleNoName(context);
  const stateContainer = context.app.get('stateContainer');
  const { name } = context.service;
  const store = context.service.store || new LRU({ max: 20 });
  context.service.store = store;
  const data = context.result.data || context.result;
  const records = Array.isArray(data) ? data : [data];

  if (context.method === 'remove') {
    records.forEach((record) => store.del(record.id));
  } else {
    records.forEach((record) => store.set(record.id, record));
  }

  await stateContainer.setState({ [name]: store.values() });

  return context;
});

export const validateSchema = skippable('validateSchema', (context) => {
  const schema = context.params.schema || context.service.schema;
  if (!schema) {
    throw new GeneralError(
      `Cannot call hook "validateSchema" on path ${context.path} because it has no schema`
    );
  }
  const dataSchema = Array.isArray(context.data)
    ? yup.array().of(schema)
    : schema;
  return dataSchema
    .validate(context.data, {
      abortEarly: false, // return all errors, not just first
      stripUnknown: true, // remove any props not on schema
      context // Pass the whole hook context...nice
    })
    .then((data) => {
      context.data = data;
      return context;
    })
    .catch((error) => {
      const errorData = convertYupError(error);
      throw new ValidationError(error.message, {
        errors: errorData
      });
    });
});

export const paginateByPage = skippable('paginateByPage', (context) => {
  if (!context.params.query || !context.params.query.$page) {
    return context;
  }
  const paginate = context.params.paginate ||
    context.service.paginate || {
      default: 10,
      max: 10
    };
  const { $page, ...query } = context.params.query;
  if (isNaN(Number($page)) || $page < 1) {
    throw new BadRequest('$page parameter must be a number greater than 1');
  }
  if ($page > 1) {
    query.$skip = $page * paginate.default - paginate.default;
  }
  context.params.query = query;
  return context;
});

// These are very, very basic cache hooks meant to be used for
// .find() methods where the output will always be the same.
// These are mainly helpful for google/travel service because
// the results will always be the same for origins/destinations
// query, and it saves LOTS of google API calls. These are also
// useful for the kinda "system" type resources that do not change
// likes roles, truck-types, shipment-statuses, etc. The default
// LRU that is setup never "expires" props (no maxAge prop) and is
// only purged by recent-ness. You can override the basic cache
// options by manually setting a cache on the service at setup time.
export const cacheBefore = async (context) => {
  if (!context.method === 'find' || !context.type === 'before') {
    throw new GeneralError(
      'The "cacheBefore" hook can only be called on before:find.'
    );
  }
  if (!context.service.cache) {
    context.service.cache = new LRU({ max: 50 });
    context.service.cache.load(
      JSON.parse(
        window.localStorage.getItem(
          `${process.env.REACT_APP_LOCAL_STORAGE_KEY}:${context.path}`
        )
      ) || []
    );
  }
  const key = JSON.stringify(context.params.query);
  const cached = context.service.cache.get(key);
  if (cached) {
    context.result = cached;
  }
  return context;
};

export const cacheAfter = async (context) => {
  if (!context.method === 'find' || !context.type === 'after') {
    throw new GeneralError(
      'The "cacheAfter" hook can only be called on after:find.'
    );
  }
  const cacheKey = JSON.stringify(context.params.query);
  const storageKey = `${process.env.REACT_APP_LOCAL_STORAGE_TOKEN}:${context.path}`;
  context.service.cache.set(cacheKey, context.result);
  try {
    // Avoid throwing when LRU grows too large and cannot be persisted
    // to localStorage due to QuotaExceededError
    // TODO: Use localForage?
    window.localStorage.setItem(
      storageKey,
      JSON.stringify(context.service.cache.dump())
    );
  } catch (error) {
    // Remove the now stale items. They are no longer least
    // recently used. Also, this would just be stuck at its current
    window.localStorage.removeItem(storageKey);
  }
  return context;
};

export const bustCache = async (context) => {
  const { cache } = context.service;
  if (cache) {
    if (cache.reset) {
      cache.reset();
    } else {
      delete context.service.cache;
    }
    window.localStorage.removeItem(
      `${process.env.REACT_APP_LOCAL_STORAGE_TOKEN}:${context.path}`
    );
  }
};

export const byUser = (context) => {
  const { user } = context.app.get('stateContainer').state;
  context.params.query = { ...context.params.query, user_id: user._id };
  return context;
};
